// preserve_token_bottom_after_size_change for a shapeshift in a never-authored (shapeless) encounter, whose rows persist a creature's z_pos
// at its visual CENTRE (bottom + height / 2). The scene draws such a row on the feet plane (encounter-creation-engine feetPlaneElevationFt:
// max(0, z − height / 2), height = the portrait token geometry), so when the form's height changes the feet stay where they are drawn and
// the stored centre moves. A depth-authored encounter persists the feet: nothing moves (owner, 2026-09-15). Deliberately no support search
// or floor snap (PHP's): that dropped a hovering creature to the ground; the feet the player sees are the truth.
import { ec, ecId, record, upper, type JanusCtx, type Row } from '../ability/context.js';
import { creatureTokenGeometry, selectCreaturePicture } from '../../shared/creature-token-geometry.js';
import { CREATURE_SIZE_TYPES } from '../../shared/creature-size-types.js';
import { encounterField, posCoord, sizeIdOf } from '../../shared/array-types.js';
import { shiftCreatureSizeId } from '../compile/creature-size.js';
import { statusItemId, statusRows } from './expiry.js';

const UUID = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/;

export function encounterIsShapeless(encounter: Row): boolean {
  const shape = encounterField<unknown>(encounter, 'encounter_shape');
  return shape == null || shape === '';
}

/** The scene's conversion height for a creature row: its portrait token height at its effective size. */
export function legacyBodyHeightFt(row: Row, sizeId: unknown = sizeIdOf(row)): number {
  return creatureTokenGeometry(row, selectCreaturePicture(row, { use_picture_thumbnail: false }), CREATURE_SIZE_TYPES, sizeId).heightFt;
}

/** The size the compile lands on with the creature-form overrides swapped (PHP lost_shapeshift's breakdown replay): the last direct set
 *  that is not a form's — or `formSize` — then every non-form step. The compile batches the steps after all direct sets, so an Enlarge cast
 *  before or during the shift re-applies to the new direct size exactly. Null when the row carries no readable breakdown. */
export function sizeWithForm(row: Row, formIds: ReadonlySet<string>, formSize: string | null): string | null {
  const breakdown = record(record(record(record(row.computed)?.creature)?.size)?.breakdown);
  let size = upper(record(breakdown?.base)?.size_id), steps = 0;
  if (!breakdown || !UUID.test(size)) return formSize && UUID.test(formSize) ? formSize : null;
  for (const event of Array.isArray(breakdown.events) ? breakdown.events : []) {
    const at = record(event);
    if (!at || formIds.has(upper(at.source_id))) continue;
    if (at.kind === 'direct_set' && UUID.test(upper(at.to_size_id))) size = upper(at.to_size_id);
    else if (at.kind === 'step_change') steps += Math.trunc(Number(at.delta)) || 0;
  }
  if (formSize && UUID.test(formSize)) size = formSize;
  return steps ? upper(shiftCreatureSizeId(size, steps)) || null : size;
}

/** The body a target takes on in `form`: the form's art (selectCreaturePicture §1) at the form's size, the held steps re-applied. */
export function formBodyHeightFt(row: Row, formId: string, form: Row, formSize: string | null): number {
  const creature = { ...record(record(row.computed)?.creature) };
  delete creature.shapeshift_pic;
  const statuses = record(row.statuses) ?? {};
  const measured: Row = { ...row, shapeshifted: [1], computed: { ...record(row.computed), creature },
    statuses: { ...statuses, target: [{ status_id: 'pending-form', status_item_id: formId, item_type: 'creature', status_arr: form }] } };
  return legacyBodyHeightFt(measured, sizeWithForm(row, new Set([formId]), formSize) ?? sizeIdOf(row));
}

/** The body a shifted target returns to: its own art at its ancestral size; null when the ancestral size cannot be read. */
export function ancestralBodyHeightFt(row: Row, formIds: ReadonlySet<string>): number | null {
  const size = sizeWithForm(row, formIds, null);
  if (!size) return null;
  const creature = { ...record(record(row.computed)?.creature) };
  delete creature.shapeshift_pic;
  const measured: Row = { ...row, computed: { ...record(row.computed), creature },
    statuses: { ...record(row.statuses), target: statusRows(row, 'target').filter(status => !formIds.has(statusItemId(status))) } };
  delete measured.shapeshifted;
  return legacyBodyHeightFt(measured, size);
}

/** Re-seat the stored centre so the drawn feet stay put; a same-height swap writes nothing (PHP's 0.01 ft no-churn rule). */
export function legacyRegroundAfterSizeChange(ctx: JanusCtx, row: Row, oldHeightFt: number, newHeightFt: number): void {
  if (!encounterIsShapeless(ctx.encounter) || !(oldHeightFt > 0) || !(newHeightFt > 0)) return;
  const z = posCoord(row, 'z_pos') ?? 0;
  const next = Math.max(0, z - oldHeightFt / 2) + newHeightFt / 2;
  if (Math.abs(next - z) <= 0.01) return;
  // removal-support.ts resolveLandingSupport's z-only settle: the member's position and its ec bucket (→ the commit's forcedMovements).
  (row.position ??= { ...record(row.pos_arr) } as Row);
  (row.position as Row).z_pos = next;
  ec(row).z_pos = next;
  const listed = (ctx.encounter.encounter_creatures_arr as Row[] | undefined)?.find(entry => ecId(entry) === ecId(row));
  if (listed && listed !== row) { const at = record(listed.position) ?? {}; at.z_pos = next; listed.position = at; }
}
